// src/ContactUsWidget.js
import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Button,
  Drawer,
  Divider,
  useTheme,
  Typography,
  TextField,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import { supabase } from './supabaseClient';

import { status, surface } from './designTokens';
const VIEW_NAME      = 'Contact Us';
const STATUS_OPTIONS = ['Open', 'Pending', 'Resolved', 'Closed'];
const PRIORITY_OPTIONS = ['High', 'Medium', 'Low'];

const statusColorMap = {
  Open:     status.dangerBg,
  Pending:  status.warningBg,
  Resolved: status.successBg,
  Closed:   status.successBg,
};
const priorityColorMap = {
  High:   status.dangerBg,
  Medium: status.warningBg,
  Low:    surface.line,
};

export default function ContactUsWidget({ phoneNumber, currentUser }) {
  const theme = useTheme();
  const [rows,       setRows]       = useState([]);
  const [detailRow,  setDetailRow]  = useState(null);
  const [comments,   setComments]   = useState([]);
  const [newComment, setNewComment] = useState('');
  const [snackbar,   setSnackbar]   = useState({ open:false, message:'', severity:'success' });

  // Add Ticket dialog state
  const [addOpen,     setAddOpen]     = useState(false);
  const [newSubject,  setNewSubject]  = useState('');
  const [newAbout,    setNewAbout]    = useState('');
  const [newPriority, setNewPriority] = useState(PRIORITY_OPTIONS[1]);

  // 1) Load tickets by phone
  const fetchData = useCallback(async () => {
    const { data, error } = await supabase
      .from(VIEW_NAME)
      .select('*')
      .eq('phone_number', phoneNumber)
      .order('created_at', { ascending:false });
    if (error) {
      console.error(error);
      setSnackbar({ open:true, message:error.message, severity:'error' });
    } else {
      setRows(data);
    }
  }, [phoneNumber]);

  // 2) When you open one ticket, fetch its comments
  useEffect(() => {
    if (!detailRow) {
      setComments([]);
      return;
    }
    supabase
      .from('ticket_comments')
      .select('id,ticket_id,author,content,created_at')
      .eq('ticket_id', detailRow.id)
      .order('created_at', { ascending:true })
      .then(({ data, error }) => {
        if (!error) setComments(data);
      });
  }, [detailRow]);

  // 3) Change status (and set closed_at if needed)
  const updateStatus = async (id, newStatus) => {
    const changes = { status: newStatus };
    if (newStatus === 'Resolved' || newStatus === 'Closed') {
      changes.closed_at = new Date().toISOString();
    }
    const { error } = await supabase
      .from(VIEW_NAME)
      .update(changes)
      .eq('id', id);
    if (error) {
      console.error(error);
      setSnackbar({ open:true, message:'Status update failed', severity:'error' });
    } else {
      fetchData();
      setDetailRow(r => r && r.id===id ? {...r, ...changes} : r);
    }
  };

  // 4) Post new comment
  const handleAddComment = async () => {
    if (!newComment.trim() || !detailRow) return;
    const entry = {
      ticket_id:  detailRow.id,
      author:     currentUser,
      content:    newComment.trim(),
      created_at: new Date().toISOString(),
    };
    const { error } = await supabase.from('ticket_comments').insert(entry);
    if (error) {
      console.error(error);
      setSnackbar({ open:true, message:'Comment save failed', severity:'error' });
    } else {
      setComments(cs => [...cs, entry]);
      setNewComment('');
    }
  };

  // 5) Add new ticket
  const handleCreateTicket = async () => {
    if (!newSubject.trim()) return;
    const ticket = {
      email:        detailRow?.email || '',       // you could default to client.email if you keep it
      phone_number: phoneNumber,
      subject:      newSubject.trim(),
      about:        newAbout.trim(),
      priority:     newPriority,
      status:       'Open',
      created_at:   new Date().toISOString(),
    };
    const { error } = await supabase.from(VIEW_NAME).insert(ticket);
    if (error) {
      console.error(error);
      setSnackbar({ open:true, message:'Ticket creation failed', severity:'error' });
    } else {
      setAddOpen(false);
      setNewSubject('');
      setNewAbout('');
      setNewPriority(PRIORITY_OPTIONS[1]);
      fetchData();
    }
  };

  useEffect(() => {
    if (phoneNumber) fetchData();
  }, [phoneNumber, fetchData]);

  const stopProp = e => e.stopPropagation();

  return (
    <>
      {/* Add Ticket + Refresh */}
      <Box sx={{ display:'flex', justifyContent:'space-between', mb:1 }}>
        <Button
          startIcon={<AddIcon />}
          size="small"
          onClick={() => setAddOpen(true)}
        >New Ticket</Button>
        <IconButton onClick={fetchData}><RefreshIcon/></IconButton>
      </Box>

      {/* Tickets Table */}
      <TableContainer component={Paper}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {['Subject','About','Priority','Status','Created At','Actions'].map(h => (
                <TableCell key={h}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(r => (
              <TableRow key={r.id} hover sx={{ cursor:'pointer' }}>
                <TableCell onClick={stopProp}>{r.subject}</TableCell>
                <TableCell onClick={stopProp}>{r.about}</TableCell>
                <TableCell onClick={stopProp}>
                  <Chip label={r.priority} size="small"
                    sx={{
                      backgroundColor: priorityColorMap[r.priority],
                      color: theme.palette.text.primary,
                      fontSize:'0.75rem', height:24
                    }}
                  />
                </TableCell>
                <TableCell onClick={stopProp}>
                  <Select
                    value={r.status} size="small"
                    onChange={e => updateStatus(r.id, e.target.value)}
                    sx={{
                      backgroundColor: statusColorMap[r.status],
                      color: theme.palette.text.primary,
                      '& .MuiSelect-icon':{ color: theme.palette.text.primary },
                      borderRadius:1, px:1,
                      fontSize:'0.75rem', height:24, minWidth:80
                    }}
                  >
                    {STATUS_OPTIONS.map(s => (
                      <MenuItem key={s} value={s}
                        sx={{
                          backgroundColor: statusColorMap[s],
                          color: theme.palette.text.primary,
                          fontSize:'0.75rem'
                        }}
                      >{s}</MenuItem>
                    ))}
                  </Select>
                </TableCell>
                <TableCell onClick={stopProp}>
                  {new Date(r.created_at).toLocaleString()}
                </TableCell>
                <TableCell onClick={stopProp}>
                  <Button
                    variant="text" size="small"
                    onClick={() => setDetailRow(r)}
                    sx={{ p:0, minWidth:0, fontSize:'0.75rem', textTransform:'none' }}
                  >View More</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Ticket Detail Drawer */}
      <Drawer
        anchor="right"
        open={Boolean(detailRow)}
        onClose={() => setDetailRow(null)}
        PaperProps={{ sx:{ width:360, p:2 } }}
      >
        {detailRow && <>
          <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'center', mb:2 }}>
            <Typography variant="h6">Ticket #{detailRow.id}</Typography>
            <IconButton onClick={() => setDetailRow(null)}><RefreshIcon/></IconButton>
          </Box>
          <Divider sx={{ mb:2 }}/>

          {/* Core Fields */}
          {[
            ['Subject',    detailRow.subject],
            ['Description',detailRow.about],
            ['Created',    new Date(detailRow.created_at).toLocaleString()],
            ['Priority',   detailRow.priority],
            ['Status',     detailRow.status],
            ['Closed At',  detailRow.closed_at
                              ? new Date(detailRow.closed_at).toLocaleString()
                              : '—'],
          ].map(([label,val])=>(
            <Box key={label} sx={{ mb:1 }}>
              <Typography variant="subtitle2">{label}</Typography>
              <Typography variant="body2">{val}</Typography>
            </Box>
          ))}

          <Divider sx={{ my:2 }}/>

          {/* Comments */}
          <Typography variant="subtitle1" gutterBottom>Comments</Typography>
          <Box sx={{ maxHeight:240, overflowY:'auto', mb:2 }}>
            {comments.map(c => (
              <Box key={c.id||c.created_at} sx={{ mb:1,p:1,borderRadius:1,bgcolor:surface.page }}>
                <Typography variant="body2" sx={{ fontWeight:500,fontSize:'0.75rem' }}>
                  {c.author} at {new Date(c.created_at).toLocaleString()}
                </Typography>
                <Typography variant="body1" sx={{ fontSize:'0.875rem' }}>
                  {c.content}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* New Comment */}
          <Box sx={{ display:'flex', gap:1 }}>
            <TextField
              fullWidth placeholder="Add comment…"
              size="small"
              value={newComment}
              onChange={e=>setNewComment(e.target.value)}
            />
            <Button variant="contained" size="small" onClick={handleAddComment}>
              Post
            </Button>
          </Box>
        </>}
      </Drawer>

      {/* Add Ticket Dialog */}
      <Dialog open={addOpen} onClose={()=>setAddOpen(false)} fullWidth>
        <DialogTitle>New Ticket for {phoneNumber}</DialogTitle>
        <DialogContent dividers>
          <TextField
            label="Subject"
            fullWidth margin="normal"
            value={newSubject}
            onChange={e=>setNewSubject(e.target.value)}
          />
          <TextField
            label="Description"
            fullWidth margin="normal"
            multiline rows={4}
            value={newAbout}
            onChange={e=>setNewAbout(e.target.value)}
          />
          <Select
            fullWidth size="small" value={newPriority}
            onChange={e=>setNewPriority(e.target.value)}
            sx={{ mt:2 }}
          >
            {PRIORITY_OPTIONS.map(p => (
              <MenuItem key={p} value={p}>{p}</MenuItem>
            ))}
          </Select>
        </DialogContent>
        <DialogActions>
          <Button onClick={()=>setAddOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateTicket}>Create</Button>
        </DialogActions>
      </Dialog>

      {/* Feedback Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={()=>setSnackbar(s=>({...s,open:false}))}
        anchorOrigin={{ vertical:'bottom', horizontal:'center' }}
      >
        <Alert severity={snackbar.severity} sx={{ width:'100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
